import type { ShippingDirection } from '@/contexts/DirectionContext';

/**
 * Etichete care se inversează în funcție de direcție.
 *
 * Pe **import** marfa sosește la Constanța (POD = ROCND), iar contrapartea
 * relevantă pentru client este shipper-ul din străinătate.
 * Pe **export** marfa pleacă din Constanța (POL = ROCND) spre destinație, iar
 * contrapartea este consignee-ul; clientul portalului e shipper-ul.
 */
export interface DirectionLabels {
  /** „ETA Constanța" pe import / fallback generic pe export. Preferă `formatEtaHeading`. */
  eta: string;
  /** Variantă scurtă pentru capete de tabel. */
  etaShort: string;
  /** „Sosire" pe import / „Plecare" pe export. */
  arrival: string;
  /** Contrapartea afișată: Shipper pe import, Consignee pe export. */
  counterparty: string;
  /** Portul „de referință" al clientului. */
  homePort: string;
}

export function directionLabels(direction: ShippingDirection, lang: 'ro' | 'en'): DirectionLabels {
  const isRo = lang === 'ro';
  const isExport = direction === 'export';

  if (isExport) {
    return {
      eta: isRo ? 'ETA destinație' : 'ETA destination',
      etaShort: 'ETA',
      arrival: isRo ? 'Plecare' : 'Departure',
      counterparty: isRo ? 'Destinatar' : 'Consignee',
      homePort: isRo ? 'Constanța (încărcare)' : 'Constanta (loading)',
    };
  }

  return {
    eta: isRo ? 'ETA Constanța' : 'ETA Constanta',
    etaShort: 'ETA',
    arrival: isRo ? 'Sosire' : 'Arrival',
    counterparty: isRo ? 'Expeditor' : 'Shipper',
    homePort: isRo ? 'Constanța (descărcare)' : 'Constanta (discharge)',
  };
}

/** Câmpuri ETA din API (listă / modal / dashboard). */
export interface EtaMeta {
  eta?: string | Date | null;
  etaPort?: string | null;
  etaPortName?: string | null;
  /** Default true pe import; pe export false = port intermediar. */
  etaIsFinal?: boolean;
  etaEstimated?: boolean;
  /** Fallback POD când etaPort lipsește dar ETA e finală. */
  pod?: string | null;
  podName?: string | null;
}

/**
 * Scoate codul din paranteze: „ISTANBUL (TRIST)" → „ISTANBUL".
 * Păstrează stringul dacă nu are formă „Nume (COD)".
 */
export function cleanPortLabel(port: string | null | undefined): string {
  if (!port) return '';
  return String(port)
    .replace(/\s*\([^)]*\)\s*$/g, '')
    .trim() || String(port).trim();
}

/**
 * Etichetă scurtă a portului pentru ETA: preferă numele (fără cod), altfel codul.
 */
export function etaPortDisplay(meta: EtaMeta | null | undefined): string | null {
  if (!meta) return null;
  const name = cleanPortLabel(meta.etaPortName);
  if (name) return name;
  const code = (meta.etaPort || '').trim();
  if (code) return code;
  if (meta.etaIsFinal !== false) {
    const podName = cleanPortLabel(meta.podName);
    if (podName) return podName;
    const pod = (meta.pod || '').trim();
    if (pod) return pod;
  }
  return null;
}

/**
 * Titlu de coloană / heading pentru ETA.
 * - import: „ETA Constanța"
 * - export: „ETA" (detaliul portului e în subtext — mai scurt)
 */
export function formatEtaHeading(
  direction: ShippingDirection,
  lang: 'ro' | 'en',
  meta?: EtaMeta | null,
): string {
  const isRo = lang === 'ro';
  if (direction !== 'export') {
    return isRo ? 'ETA Constanța' : 'ETA Constanta';
  }
  // Coloană scurtă; detaliul portului e pe linia de sub dată.
  if (meta?.etaIsFinal === false) return 'ETA';
  const port = etaPortDisplay(meta || undefined);
  if (port) return `ETA ${port}`;
  return 'ETA';
}

/**
 * Subtext lângă data ETA.
 * Export intermediar: „SINGAPORE · port de transshipment".
 * Nu adăugăm „estimat" — ETA = Estimated Time of Arrival.
 * Import: gol (heading e deja Constanța).
 */
export function formatEtaSubtext(
  direction: ShippingDirection,
  lang: 'ro' | 'en',
  meta?: EtaMeta | null,
): string {
  if (!meta?.eta) return '';
  const isRo = lang === 'ro';

  if (direction === 'export' && meta.etaIsFinal === false) {
    const port = etaPortDisplay(meta) || (isRo ? 'port' : 'port');
    return isRo ? `${port} · port de transshipment` : `${port} · transshipment port`;
  }

  return '';
}
